import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'

import {
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
  ThemeConfigInterface,
} from '@store/types'

import { CanvasInteractive } from '@components/showcase-canvas'
import { CanvasRefInterface } from '@components/showcase-canvas/canvas-interactive'
import {
  InactiveColor,
  MarkerType,
} from '@components/showcase-canvas/constants'
import { Polygon } from '@components/showcase-canvas/types'

import { BuildingInterface, Level } from '@api/building'
import { MapContent, MappingCollection } from '@api/interactive-plan'

import analyzePixels from '@utilities/analyze-pixels'
import styleUtil from '@utilities/style-util'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import filterUnit from '@adUtilities/filter-unit'
import { slugify } from '@adUtilities/helper'
import { Unit } from '@adUtilities/types/apartment'

export interface FloorplanCanvasProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  firebaseActiveBlock: string
  firebaseActiveLevel: string
  building: BuildingInterface
  floorplan: MappingCollection
  unitFilter: ExtendedUnitFilterOptions
  fullScreenToggle: boolean
  ratio?: string
}

function FloorplanCanvas({
  projectIdentity,
  session,
  firebaseActiveBlock,
  firebaseActiveLevel,
  building,
  floorplan,
  unitFilter,
  fullScreenToggle,
  ratio = 'max',
}: FloorplanCanvasProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const {
    showPrice,
    propertyPrefix,
    disableSoldProperty,
    statusLabels,
    statusColors,
  } = projectIdentity

  const themeData = styleUtil()

  const prevReference = useRef<{
    activeLevel: string
    activeBlock: string
    activeImage: string
  }>({
    activeLevel: firebaseActiveLevel,
    activeBlock: firebaseActiveBlock,
    activeImage: '',
  })

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<CanvasRefInterface>()

  const [isFloorPlanImageLoaded, setImageLoaded] = useState<boolean>(false)
  const [renderCanvas, setRenderCanvas] = useState<boolean>(false)
  const [backgroundColor, setBackgroundColor] =
    useState<string>('rgb(255, 255, 255)')

  const [units, setUnits] = useState<Unit[]>([])

  const [theme, setTheme] = useState<
    Pick<ThemeConfigInterface, 'font' | 'mainColour'>
  >({
    font: '',
    mainColour: '',
  })

  const [floorplanMap, setFloorPlanMap] = useState<MapContent>()

  const hasFloorplan = () => Boolean(Object.keys(floorplan).length)

  const arrowMarkerFilterLogic = (unit: Unit) => {
    const { apply } = unitFilter

    if (!apply) {
      return false
    }

    return !filterUnit(unit, unitFilter, showPrice, statusLabels?.available)
  }

  const badgeAndNumberedFilterLogic = (unit: Unit) => {
    const { apply } = unitFilter

    if (!apply) {
      return false
    }

    return !filterUnit(unit, unitFilter, showPrice, statusLabels?.available)
  }

  const checkUnitStatus = (unit: Unit, type: string) => {
    if (disableSoldProperty && unit.metas.status !== statusLabels?.available) {
      return true
    }
    if (
      type === MarkerType.arrowSmall ||
      type === MarkerType.arrow ||
      type === MarkerType.oblongLabel ||
      type === MarkerType.oblongStatus
    ) {
      return arrowMarkerFilterLogic(unit)
    }
    return badgeAndNumberedFilterLogic(unit)
  }

  const isSoldUnitDisabled = (unit: Unit) => {
    if (disableSoldProperty && unit.metas.status !== statusLabels?.available) {
      return true
    }
    return !unit?.metas
  }

  const setUnitColor = (status: string, poly: Polygon) => {
    if (
      poly.type === MarkerType.arrowSmall ||
      poly.type === MarkerType.oblongStatus
    ) {
      return poly?.color
    }

    if (poly.type === MarkerType.badge) {
      return InactiveColor
    }

    if (status === statusLabels?.available) {
      return poly?.color || '#0abec4'
    }
    return InactiveColor
  }

  const getMarkerColour = (status: string) =>
    statusColors[slugify(status)] || '#fff'

  const handleUnitData = (poly: Polygon) => {
    const unit = units.find(
      (res) => res.name.replace(/\s/g, '') === poly?.groupId?.replace(/\s/g, '')
    )

    return {
      disabled: unit && isSoldUnitDisabled(unit),
      postFix: unit?.metas.status,
      color: setUnitColor(unit?.metas.status || '', poly),
      markerColor: getMarkerColour(unit?.metas.status || ''),
      activeByDefault: unit && checkUnitStatus(unit, poly?.type || ''),
    }
  }

  const mapFloorplanActions = React.useCallback(() => {
    const mapName = firebaseActiveBlock
      ? `${firebaseActiveBlock}-${firebaseActiveLevel}`
      : firebaseActiveLevel

    const currentMap: MapContent = floorplan[mapName]
    if (!currentMap) {
      return
    }

    setFloorPlanMap({
      ...currentMap,
      image: currentMap.image,
      polygons: currentMap.polygons
        .map((poly) => ({
          ...poly,
          ...handleUnitData(poly),
          onClick: () => {},
          onComplete: async () => {
            if (poly.groupId) {
              await firebaseControlQuery.update({
                [`building.activeUnit`]: poly.groupId,
              })
            }
          },
        }))
        .filter((res) =>
          units.find(
            (resInner) =>
              resInner.name.replace(/\s/g, '') ===
              res?.groupId?.replace(/\s/g, '')
          )
        ),
    })
  }, [
    firebaseActiveLevel,
    firebaseActiveBlock,
    unitFilter,
    units,
    statusLabels,
  ])

  const handleBackgroundColor = () => {
    if (floorplanMap) {
      const url = AssetHandler({
        url: floorplanMap.image,
        type: 'new',
        noSpliceUrl: true,
      })
      const dynamicImage = document.createElement('img')
      dynamicImage.crossOrigin = 'Anonymous'
      dynamicImage.src = url
      dynamicImage.onload = () => {
        const color = analyzePixels(dynamicImage)
        const rgb = `rgb(${color[0]}, ${color[1]}, ${color[2]})`
        setBackgroundColor(rgb)
      }
    }
  }

  const handleCanvasItems = () => {
    canvasRef?.current?.setPolyActive()
  }

  const findUnits = () =>
    building.levels
      .find((level: Level) => level.level === firebaseActiveLevel)
      ?.data?.filter((unit) => {
        if (firebaseActiveBlock) {
          return unit.blockId === firebaseActiveBlock
        }
        return true
      }) || []

  useLayoutEffect(() => {
    handleBackgroundColor()
  }, [firebaseActiveLevel, firebaseActiveBlock])

  useEffect(() => {
    setUnits(findUnits())
  }, [building, firebaseActiveBlock, firebaseActiveLevel])

  useEffect(() => {
    if (hasFloorplan() && canvasContainerRef && units.length > 0) {
      mapFloorplanActions()
      setRenderCanvas(true)
    }
  }, [
    firebaseActiveLevel,
    firebaseActiveBlock,
    unitFilter,
    units,
    statusLabels,
  ])

  useEffect(() => {
    if (!floorplanMap) {
      return
    }
    if (
      firebaseActiveLevel !== prevReference.current.activeLevel ||
      firebaseActiveBlock !== prevReference.current.activeBlock ||
      floorplanMap.image !== prevReference.current.activeImage
    ) {
      setTimeout(() => canvasRef?.current?.setCanvas(), 200)
    }
    prevReference.current = {
      ...prevReference.current,
      activeLevel: firebaseActiveLevel,
      activeBlock: firebaseActiveBlock,
      activeImage: floorplanMap.image,
    }
    handleCanvasItems()
  }, [floorplanMap])

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      building: { activeUnit },
    } = session

    canvasRef?.current?.artificialTrigger(activeUnit)
  }, [session])

  useEffect(() => {
    const themeFromStorage = JSON.parse(
      localStorage.getItem('themeObject') || '{}'
    )
    if (themeFromStorage) {
      setTheme(themeFromStorage)
    }
  }, [])

  useEffect(() => {
    setImageLoaded(false)
    requestAnimationFrame(() => {
      canvasRef?.current?.setCanvas()
    })
  }, [fullScreenToggle])

  return (
    <div
      className="relative h-full w-full bg-white"
      style={{ backgroundColor }}
    >
      <div className="h-full w-full">
        <div
          className={`relative flex aspect-video h-full w-screen items-center justify-center transition ease-in-out ${
            isFloorPlanImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={
            !fullScreenToggle ? { maxWidth: 'calc(100vw - 297px)' } : undefined
          }
          ref={canvasContainerRef}
        >
          {floorplanMap && renderCanvas && (
            <CanvasInteractive
              ref={canvasRef}
              id="floorplan-canvas"
              canvasData={floorplanMap}
              parentRef={canvasContainerRef}
              labelPrefix={`${propertyPrefix || 'Unit'}:`}
              ratio={ratio}
              theme={{
                brandColour: theme?.mainColour || themeData.mainColour || '',
                font: theme?.font || themeData.font || '',
              }}
              adjustCanvasSizeWithContainer
              setImageLoaded={() => {
                setTimeout(() => {
                  setImageLoaded(true)
                }, 300)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
  })
)(FloorplanCanvas)
